import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCreditsUsage } from "@/hooks/useCreditsUsage";
import { useStorageUsage } from "@/hooks/useStorageUsage";

interface LimitReachedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'credits' | 'storage';
}

export const LimitReachedDialog = ({ open, onOpenChange, type }: LimitReachedDialogProps) => {
  const navigate = useNavigate();
  const { creditUsage, formatCredits } = useCreditsUsage();
  const { storageData, formatSize } = useStorageUsage();

  const handleUpgrade = () => {
    navigate('/planos');
    onOpenChange(false);
  };

  const handleManageFiles = () => {
    navigate('/casos');
    onOpenChange(false);
  };

  if (type === 'credits') {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-destructive">
              🚫 Créditos Esgotados
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p className="text-base">
                Você atingiu o limite de créditos do seu plano.
              </p>
              
              {creditUsage && (
                <div className="bg-muted p-3 rounded-md space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Usado:</span>
                    <span className="font-bold">{formatCredits(creditUsage.total_credits)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Limite:</span>
                    <span>{formatCredits(creditUsage.limit_credits)}</span>
                  </div>
                </div>
              )}

              <p className="text-sm">
                Seus créditos serão renovados automaticamente no início do próximo mês, ou você pode:
              </p>

              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Assinar um plano superior com mais créditos</li>
                <li>Aguardar a renovação mensal</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full sm:w-auto"
            >
              Entendi
            </Button>
            <AlertDialogAction
              onClick={handleUpgrade}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90"
            >
              Ver Planos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Storage limit dialog
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-bold text-destructive">
            💾 Armazenamento Cheio
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3 pt-2">
            <p className="text-base">
              Você atingiu o limite de armazenamento do seu plano.
            </p>
            
            {storageData && (
              <div className="bg-muted p-3 rounded-md space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Usado:</span>
                  <span className="font-bold">{formatSize(storageData.storageUsed)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Limite:</span>
                  <span>{formatSize(storageData.storageLimit)}</span>
                </div>
              </div>
            )}

            <p className="text-sm">
              Para continuar enviando arquivos, você pode:
            </p>

            <ul className="list-disc list-inside text-sm space-y-1 ml-2">
              <li>Excluir conversas antigas com arquivos pesados</li>
              <li>Assinar um plano superior com mais espaço</li>
            </ul>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleManageFiles}
            className="w-full sm:w-auto"
          >
            Gerenciar Arquivos
          </Button>
          <AlertDialogAction
            onClick={handleUpgrade}
            className="w-full sm:w-auto bg-primary hover:bg-primary/90"
          >
            Ver Planos
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};